import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LuCheck, LuLoaderCircle } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { RootState } from '@/types/graph';

export const FakeDataGatheringAnimation = ({
  onComplete,
}: {
  onComplete: () => void;
}) => {
  const [validatedBlocks, setValidatedBlocks] = useState<number[]>([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const [shouldFadeOutPage, setShouldFadeOutPage] = useState(false);
  const dataBlocks = ['Motifs', 'Antécédents', 'Prévention'];
  const professional = useSelector(
    (state: RootState) => state.consultation.professional,
  );

  useEffect(() => {
    dataBlocks.forEach((_, index) => {
      setTimeout(
        () => {
          setValidatedBlocks((prev) => [...prev, index]);

          // If it's the last block, trigger the fade-out after 1s
          if (index === dataBlocks.length - 1) {
            setTimeout(() => {
              setShouldFadeOut(true);
              setIsCompleted(true);
              setTimeout(() => {
                setShouldFadeOutPage(true);
                setTimeout(() => {
                  onComplete?.();
                }, 300); // Page fade-out duration
              }, 1500); // Wait before page blocks fade-out
            }, 1000);
          }
        },
        600 + index * 1200,
      );
    });
  }, [onComplete]);

  return (
    <motion.div
      className="flex flex-col items-center justify-center w-full h-full"
      animate={{
        opacity: shouldFadeOutPage ? 0 : 1,
      }}
      transition={{
        duration: 0.3,
        ease: 'easeOut',
      }}
    >
      <div className="flex items-center justify-center gap-3 w-full">
        <LuLoaderCircle className="h-5 w-5 text-[#5f6368] animate-spin" />
        <p className="font-medium text-[#5f6368]">
          {isCompleted
            ? `Transmission ${
                professional
                  ? `au ${professional.title} ${professional.lastname}`
                  : 'à votre médecin'
              }`
            : 'Préparation pour votre médecin'}
        </p>
      </div>

      <div className="relative w-full max-w-70 mt-6 min-h-36">
        {dataBlocks.map((block, index) => (
          <AnimatePresence key={block}>
            {validatedBlocks.includes(index) && (
              <motion.div
                initial={{ opacity: 0, y: 0 }}
                animate={{
                  opacity: shouldFadeOut ? 0 : 1,
                  y: shouldFadeOut ? -20 : index * 48,
                }}
                transition={{
                  y: { duration: 0.6, ease: 'easeOut' },
                  opacity: { duration: 0.4 },
                }}
                className="absolute top-0 left-0 right-0 flex items-center justify-between py-1"
              >
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="font-medium text-[#22c55e]"
                >
                  {block}
                </motion.span>

                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 25,
                    delay: 0.3,
                  }}
                >
                  <LuCheck
                    className="h-4 w-4 text-[#22c55e]"
                    style={{ strokeWidth: 2.5 }}
                  />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        ))}
      </div>
    </motion.div>
  );
};
